'use client';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import type MuxPlayerElement from '@mux/mux-player';
import Icon from '@/components/ui/AppIcon';
import { playbackService } from '@/lib/services/playbackService';

interface MuxPlayerModalProps {
  playbackId: string;
  title: string;
  contentRating?: string;
  contentFlags?: string[];
  filmId?: string;
  isTrailer?: boolean;
  startTime?: number;
  onClose: () => void;
  onTimeUpdate?: (seconds: number) => void;
}

const RESOLUTIONS = [
  { label: 'Auto', value: undefined },
  { label: '1080p', value: '1080p' as const },
  { label: '720p', value: '720p' as const },
  { label: '480p', value: '480p' as const },
] as const;

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function MuxPlayerModal({
  playbackId,
  title,
  contentRating,
  contentFlags = [],
  filmId,
  isTrailer = false,
  startTime = 0,
  onClose,
  onTimeUpdate,
}: MuxPlayerModalProps) {
  const playerRef = useRef<MuxPlayerElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const controlsHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track total seconds watched and end time for session recording
  const totalSecondsWatchedRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(startTime);
  const sessionSavedRef = useRef<boolean>(false);
  const pausedRef = useRef<boolean>(true);

  const [paused, setPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(startTime);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showResMenu, setShowResMenu] = useState(false);
  const [maxResolution, setMaxResolution] = useState<'720p' | '1080p' | '1440p' | '2160p' | undefined>(undefined);
  const [selectedResLabel, setSelectedResLabel] = useState('Auto');
  const [resumeBanner, setResumeBanner] = useState(startTime > 5);
  const [buffered, setBuffered] = useState(0);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);

  // ── Auto-hide controls ──────────────────────────────────────────────────
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (controlsHideTimer.current) clearTimeout(controlsHideTimer.current);
    controlsHideTimer.current = setTimeout(() => {
      if (!paused) setShowControls(false);
    }, 3000);
  }, [paused]);

  useEffect(() => {
    resetControlsTimer();
    return () => {
      if (controlsHideTimer.current) clearTimeout(controlsHideTimer.current);
    };
  }, [resetControlsTimer]);

  // ── Periodic save ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!filmId || isTrailer) return;
    saveTimerRef.current = setInterval(() => {
      const t = playerRef.current?.currentTime ?? 0;
      if (t > 0) onTimeUpdate?.(t);
    }, 10000);
    return () => {
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
  }, [filmId, isTrailer, onTimeUpdate]);

  // ── Save playback session on unmount ────────────────────────────────────
  useEffect(() => {
    return () => {
      if (!filmId || isTrailer || sessionSavedRef.current) return;
      const endTime = playerRef.current?.currentTime ?? lastTimeRef.current;
      const totalWatched = totalSecondsWatchedRef.current;
      const dur = playerRef.current?.duration ?? 0;
      if (totalWatched > 0) {
        sessionSavedRef.current = true;
        playbackService.saveSession({
          filmId,
          endTimeSeconds: endTime,
          totalSecondsWatched: totalWatched,
          durationSeconds: dur,
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filmId, isTrailer]);

  // ── Fullscreen change listener ──────────────────────────────────────────
  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  // ── Captions helpers ────────────────────────────────────────────────────
  // captionsEnabledRef mirrors state so event listeners always see current value
  const captionsEnabledRef = useRef(false);

  const applyCaptions = useCallback((enabled: boolean) => {
    const player = playerRef.current;
    if (!player) return;
    const video = (player as any).media?.nativeEl as HTMLVideoElement | undefined
      ?? player.shadowRoot?.querySelector('video') as HTMLVideoElement | undefined
      ?? (player as any).querySelector?.('video') as HTMLVideoElement | undefined;
    if (!video) return;
    const tracks = video.textTracks;
    if (!tracks || tracks.length === 0) return;
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      if (track.kind === 'subtitles' || track.kind === 'captions') {
        track.mode = enabled ? 'showing' : 'hidden';
      }
    }
  }, []);

  // ── Ensure captions are off by default and stay off unless user enables them ──
  useEffect(() => {
    // Helper to get the underlying <video> element from the Mux player
    const getVideo = (): HTMLVideoElement | null => {
      const player = playerRef.current;
      if (!player) return null;
      return (
        (player as any).media?.nativeEl as HTMLVideoElement | null
        ?? player.shadowRoot?.querySelector('video') as HTMLVideoElement | null
        ?? (player as any).querySelector?.('video') as HTMLVideoElement | null
      );
    };

    // Hide all caption/subtitle tracks immediately
    const hideAllTracks = (trackList: TextTrackList) => {
      for (let i = 0; i < trackList.length; i++) {
        const t = trackList[i];
        if ((t.kind === 'subtitles' || t.kind === 'captions') && !captionsEnabledRef.current) {
          t.mode = 'hidden';
        }
      }
    };

    // Called whenever a new track is added — hide it unless user turned CC on
    const onAddTrack = (e: TrackEvent) => {
      const track = e.track as TextTrack;
      if ((track.kind === 'subtitles' || track.kind === 'captions') && !captionsEnabledRef.current) {
        track.mode = 'hidden';
      }
    };

    let trackList: TextTrackList | null = null;

    const attachListeners = () => {
      const video = getVideo();
      if (!video) return false;
      trackList = video.textTracks;
      hideAllTracks(trackList);
      trackList.addEventListener('addtrack', onAddTrack as EventListener);
      return true;
    };

    // Try immediately, then retry until the video element is available
    if (!attachListeners()) {
      const t0 = setTimeout(() => { if (!attachListeners()) {
        const t1 = setTimeout(() => { if (!attachListeners()) {
          const t2 = setTimeout(attachListeners, 1000);
          return () => clearTimeout(t2);
        }}, 500);
        return () => clearTimeout(t1);
      }}, 100);
      return () => clearTimeout(t0);
    }

    return () => {
      if (trackList) trackList.removeEventListener('addtrack', onAddTrack as EventListener);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCaptions = useCallback(() => {
    const next = !captionsEnabled;
    captionsEnabledRef.current = next;
    setCaptionsEnabled(next);
    applyCaptions(next);
  }, [captionsEnabled, applyCaptions]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const player = playerRef.current;
      if (!player) return;
      switch (e.code) {
        case 'Space': case'KeyK':
          e.preventDefault();
          player.paused ? player.play() : player.pause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          player.currentTime = Math.min(player.currentTime + 10, player.duration || 0);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          player.currentTime = Math.max(player.currentTime - 10, 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          player.volume = Math.min((player.volume ?? 1) + 0.1, 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          player.volume = Math.max((player.volume ?? 1) - 0.1, 0);
          break;
        case 'KeyM':
          e.preventDefault();
          player.muted = !player.muted;
          break;
        case 'KeyF':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'KeyC':
          e.preventDefault();
          toggleCaptions();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, toggleCaptions]);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      await containerRef.current.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    if (playerRef.current) playerRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    if (playerRef.current) {
      playerRef.current.volume = v;
      playerRef.current.muted = v === 0;
    }
    setVolume(v);
    setMuted(v === 0);
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    const newMuted = !muted;
    playerRef.current.muted = newMuted;
    setMuted(newMuted);
  };

  const selectResolution = (label: string, value: typeof maxResolution) => {
    setMaxResolution(value);
    setSelectedResLabel(label);
    setShowResMenu(false);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const volumeIcon =
    muted || volume === 0 ? 'SpeakerXMarkIcon' :
    volume < 0.5 ? 'SpeakerWaveIcon' : 'SpeakerWaveIcon';

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      {/* Backdrop click to close */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Player container — fills full width */}
      <div
        ref={containerRef}
        className="relative w-full h-full z-10"
        onMouseMove={resetControlsTimer}
        onMouseLeave={() => { if (!paused) setShowControls(false); }}
        style={{ cursor: showControls ? 'default' : 'none' }}
      >
        {/* MuxPlayer — full width */}
        <MuxPlayer
          ref={playerRef as any}
          playbackId={playbackId}
          startTime={startTime}
          maxResolution={maxResolution}
          metadata={{ video_title: title }}
          autoPlay
          nohotkeys
          style={{
            '--controls': 'none',
            width: '100vw',
            height: '100vh',
            display: 'block',
            position: 'absolute',
            inset: 0,
            objectFit: 'contain',
          } as React.CSSProperties}
          onPlay={() => { setPaused(false); pausedRef.current = false; }}
          onPause={() => { setPaused(true); pausedRef.current = true; setShowControls(true); }}
          onTimeUpdate={(e: any) => {
            const t = (e.target as HTMLVideoElement).currentTime ?? 0;
            // Accumulate watched seconds (only when playing forward) — use ref to avoid stale closure
            if (!pausedRef.current && t > lastTimeRef.current && t - lastTimeRef.current < 5) {
              totalSecondsWatchedRef.current += t - lastTimeRef.current;
            }
            lastTimeRef.current = t;
            setCurrentTime(t);
            const buf = (e.target as HTMLVideoElement).buffered;
            if (buf && buf.length > 0) setBuffered((buf.end(buf.length - 1) / ((e.target as HTMLVideoElement).duration || 1)) * 100);
          }}
          onDurationChange={(e: any) => setDuration((e.target as HTMLVideoElement).duration ?? 0)}
          onVolumeChange={(e: any) => {
            const el = e.target as HTMLVideoElement;
            setVolume(el.volume ?? 1);
            setMuted(el.muted ?? false);
          }}
          onEnded={() => {
            setPaused(true);
            setShowControls(true);
            // Save session on natural end
            if (filmId && !isTrailer && !sessionSavedRef.current) {
              sessionSavedRef.current = true;
              const dur = playerRef.current?.duration ?? duration;
              playbackService.saveSession({
                filmId,
                endTimeSeconds: dur,
                totalSecondsWatched: totalSecondsWatchedRef.current,
                durationSeconds: dur,
              });
            }
            if (!isTrailer) onTimeUpdate?.(0);
          }}
        />

        {/* Click-to-play/pause on video area — sits BEHIND controls overlay */}
        <div
          className="absolute inset-0 z-0"
          onClick={() => {
            playerRef.current?.paused ? playerRef.current.play() : playerRef.current?.pause();
            resetControlsTimer();
          }}
        />

        {/* ── Custom Controls Overlay ── */}
        <div
          className={`absolute inset-0 flex flex-col transition-opacity duration-300 z-10 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top bar — film name, rating, flags */}
          <div
            className="flex items-start gap-3 px-4 pt-4 pb-10"
            style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.80) 0%, transparent 100%)' }}
          >
            {/* Film info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-bold text-xl sm:text-2xl md:text-3xl leading-tight">
                {title}{isTrailer ? ' — Trailer' : ''}
              </h2>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                {contentRating && (
                  <span className="border border-white/40 text-white/90 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide">
                    {contentRating}
                  </span>
                )}
                {contentFlags.map((flag) => (
                  <span
                    key={flag}
                    className="bg-white/10 border border-white/20 text-white/75 text-[10px] font-medium px-1.5 py-0.5 rounded capitalize"
                  >
                    {flag}
                  </span>
                ))}
              </div>
            </div>

            {/* Close button — top right */}
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors shrink-0 mt-0.5"
              aria-label="Close"
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>

          {/* Spacer — transparent, no click handler */}
          <div className="flex-1" />

          {/* Resume banner */}
          {resumeBanner && startTime > 5 && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 bg-black/80 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-3 text-sm text-white shadow-xl pointer-events-auto">
              <Icon name="ClockIcon" size={16} className="text-primary shrink-0" />
              <span>Resuming from <strong>{formatTime(startTime)}</strong></span>
              <button
                onClick={() => { if (playerRef.current) playerRef.current.currentTime = 0; setResumeBanner(false); }}
                className="text-xs text-white/60 hover:text-white underline ml-1 transition-colors"
              >
                Start over
              </button>
              <button onClick={() => setResumeBanner(false)} className="text-white/50 hover:text-white ml-1 transition-colors">
                <Icon name="XMarkIcon" size={14} />
              </button>
            </div>
          )}

          {/* Bottom controls */}
          <div
            className="flex flex-col px-3 pb-4"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)' }}
          >
            {/* Progress bar */}
            <div className="px-1 pb-1 group/progress">
              <div className="relative h-1 group-hover/progress:h-2.5 transition-all duration-150 rounded-full bg-white/20 cursor-pointer">
                <div className="absolute inset-y-0 left-0 rounded-full bg-white/30" style={{ width: `${buffered}%` }} />
                <div className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${progress}%` }} />
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  step={0.5}
                  value={currentTime}
                  onChange={handleSeek}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  aria-label="Seek"
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-md opacity-0 group-hover/progress:opacity-100 transition-opacity pointer-events-none"
                  style={{ left: `calc(${progress}% - 6px)` }}
                />
              </div>
            </div>

            {/* Controls row */}
            <div className="flex items-center gap-1 pt-1">
              {/* Play / Pause */}
              <button
                onClick={() => playerRef.current?.paused ? playerRef.current.play() : playerRef.current?.pause()}
                className="w-9 h-9 flex items-center justify-center text-white hover:text-primary transition-colors rounded-full hover:bg-white/10"
                aria-label={paused ? 'Play' : 'Pause'}
              >
                <Icon name={paused ? 'PlayIcon' : 'PauseIcon'} size={20} />
              </button>

              {/* Skip back 10s */}
              <button
                onClick={() => { if (playerRef.current) playerRef.current.currentTime = Math.max(currentTime - 10, 0); }}
                className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
                aria-label="Rewind 10 seconds"
              >
                <Icon name="BackwardIcon" size={16} />
              </button>

              {/* Skip forward 10s */}
              <button
                onClick={() => { if (playerRef.current) playerRef.current.currentTime = Math.min(currentTime + 10, duration); }}
                className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
                aria-label="Forward 10 seconds"
              >
                <Icon name="ForwardIcon" size={16} />
              </button>

              {/* Volume */}
              <div
                className="relative flex items-center"
                onMouseEnter={() => setShowVolumeSlider(true)}
                onMouseLeave={() => setShowVolumeSlider(false)}
              >
                <button
                  onClick={toggleMute}
                  className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
                  aria-label={muted ? 'Unmute' : 'Mute'}
                >
                  <Icon name={volumeIcon} size={18} />
                </button>
                <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 transition-all duration-150 ${showVolumeSlider ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                  <div className="bg-black/80 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-3 flex flex-col items-center gap-1">
                    <span className="text-white text-xs font-mono">{muted ? '0' : Math.round(volume * 100)}%</span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.02}
                      value={muted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="h-20 cursor-pointer accent-primary"
                      style={{ writingMode: 'vertical-lr', direction: 'rtl', WebkitAppearance: 'slider-vertical' } as React.CSSProperties}
                      aria-label="Volume"
                    />
                  </div>
                </div>
              </div>

              {/* Time display */}
              <span className="text-white/70 text-xs font-mono ml-1 select-none">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Captions toggle (CC) */}
              <button
                onClick={toggleCaptions}
                className={`flex items-center gap-1 text-xs font-bold px-2 py-1.5 rounded transition-colors ${
                  captionsEnabled
                    ? 'text-yellow-400 bg-yellow-400/20 hover:bg-yellow-400/30' :'text-white/60 hover:text-white hover:bg-white/10'
                }`}
                aria-label={captionsEnabled ? 'Disable captions' : 'Enable captions'}
                title="Captions (C)"
              >
                <span className="font-mono tracking-tight">CC</span>
              </button>

              {/* Resolution picker */}
              {!isTrailer && (
                <div className="relative">
                  <button
                    onClick={() => setShowResMenu(v => !v)}
                    className="flex items-center gap-1 text-white/70 hover:text-white text-xs font-semibold px-2 py-1.5 rounded hover:bg-white/10 transition-colors"
                    aria-label="Resolution"
                  >
                    <Icon name="Cog6ToothIcon" size={15} />
                    <span className="hidden sm:inline">{selectedResLabel}</span>
                  </button>
                  {showResMenu && (
                    <div className="absolute bottom-full right-0 mb-2 bg-black/90 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden shadow-xl min-w-[100px]">
                      {RESOLUTIONS.map(r => (
                        <button
                          key={r.label}
                          onClick={() => selectResolution(r.label, r.value)}
                          className={`w-full text-left px-4 py-2 text-xs transition-colors hover:bg-white/10 ${selectedResLabel === r.label ? 'text-primary font-semibold' : 'text-white/80'}`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                <Icon name={isFullscreen ? 'ArrowsPointingInIcon' : 'ArrowsPointingOutIcon'} size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
